import React from "react";
import { Button, Space, Typography } from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import {
  DosificationSlot,
  DosificationSlotCourse,
} from "../../../../services/school-dosification/school-dosification.types";
import SortableList from "../../../SortableList";

const { Text } = Typography;

interface SlotCourseRowProps {
  course: DosificationSlotCourse;
  /** Only when the week holds several courses and they can be reordered. */
  dragHandle?: React.ReactNode;
  saving: boolean;
  onChange: () => void;
  onRemove: () => void;
}

// The buttons live inside the row so they move with the course when dragging.
const SlotCourseRow: React.FC<SlotCourseRowProps> = ({
  course,
  dragHandle,
  saving,
  onChange,
  onRemove,
}) => (
  <div
    style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 0" }}
  >
    {dragHandle}
    <Space direction="vertical" size={0} style={{ flex: 1, minWidth: 0 }}>
      <Text>{course.title}</Text>
      {course.source_program?.grade && (
        // El badge es sólo para el admin: el profesor no ve de qué grado viene
        // el curso.
        <Text type="secondary" style={{ fontSize: 12 }}>
          Curso original: {course.source_program.grade.grade}
        </Text>
      )}
    </Space>
    <Space size={4}>
      <Button size="small" disabled={saving} onClick={onChange}>
        Cambiar curso
      </Button>
      <Button
        size="small"
        danger
        icon={<DeleteOutlined />}
        disabled={saving}
        onClick={onRemove}
      >
        Quitar
      </Button>
    </Space>
  </div>
);

interface SlotCoursesCellProps {
  slot: DosificationSlot;
  saving: boolean;
  /** Opens the catalogue to replace the course at `index`. */
  onChange: (index: number) => void;
  onRemove: (index: number) => void;
  /** Receives the week's courses in their new order after a drop. */
  onReorder: (courses: DosificationSlotCourse[]) => void;
}

/**
 * SCRUM-1076: the «Curso asignado» cell. A week can hold several sequences,
 * stacked in the order the platform shows them; each one is changed or
 * removed on its own, and with two or more they can be reordered by drag and
 * drop.
 */
const SlotCoursesCell: React.FC<SlotCoursesCellProps> = ({
  slot,
  saving,
  onChange,
  onRemove,
  onReorder,
}) => {
  if (slot.is_empty) {
    return <Text type="secondary">Sin curso</Text>;
  }

  const courses = slot.assigned_courses;

  if (courses.length === 0) {
    return <Text type="secondary">—</Text>;
  }

  // A single course has nothing to reorder: no handle, same row as always.
  if (courses.length === 1) {
    return (
      <SlotCourseRow
        course={courses[0]}
        saving={saving}
        onChange={() => onChange(0)}
        onRemove={() => onRemove(0)}
      />
    );
  }

  return (
    <SortableList
      items={courses}
      onReorder={onReorder}
      // The backend rejects the same course twice in a week, so its id is a
      // stable key within the list.
      getId={(course) => course.course_id}
      renderItem={(course, index, { dragHandle }) => (
        <SlotCourseRow
          course={course}
          dragHandle={dragHandle}
          saving={saving}
          onChange={() => onChange(index)}
          onRemove={() => onRemove(index)}
        />
      )}
    />
  );
};

export default SlotCoursesCell;
